/**
 * ReviewDetailActions — the one transition available on a flagged item
 * (BON-381), in the page header on desktop and in a fixed bottom bar on a phone
 * so it is reachable without scrolling back up.
 *
 * `Mark resolved` / `Reopen` OPEN THE NOTE COMPOSER; neither commits on its own,
 * because both require a note. While the composer is open the button is GONE
 * rather than disabled-but-clickable: pressing it again could only re-open what
 * is already open, and a control that looks live and does nothing reads as
 * broken. Both are hidden from a viewer without `write:payment-reviews`,
 * mirroring the server guard rather than offering a button that would 403.
 *
 * The old `Open booking` / `Stripe` buttons are gone from here: they duplicated
 * two of the Linked resources rows on the same screen, and that card is the
 * better home because it also carries the customer and the workshop — one place
 * that answers "where do I go next".
 */
import { Check, RotateCcw } from 'lucide-react';

import type { ReviewDetail } from './reviewTypes';
import type { ComposerMode } from './useReviewDetailState';

export interface ReviewDetailActionsProps {
  detail: ReviewDetail;
  canWrite: boolean;
  /** True while the note composer is open — the trigger stands down. */
  composing: boolean;
  onCompose: (mode: ComposerMode) => void;
  /** 'bar' pins the action to the bottom of the phone viewport. */
  variant?: 'header' | 'bar';
}

const BAR_STYLE = {
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 20,
  display: 'flex',
  gap: 10,
  padding: '10px 14px',
  borderTop: '1px solid var(--nb-border)',
  background: 'var(--nb-bg)',
} as const;

const HEADER_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
} as const;

export function ReviewDetailActions({
  detail,
  canWrite,
  composing,
  onCompose,
  variant = 'header',
}: ReviewDetailActionsProps) {
  // Nothing to draw: no entitlement, or the composer already has the floor.
  if (!canWrite || composing) return null;

  // A SUPERSEDED item has no action, and that is not an omission. Its flag moved
  // to the booking fulfillment created, so "Reopen" would put a false item back
  // in the queue — the incident is live on the booking, and the operator should
  // be working there. `!needsReview` used to decide this, which offered exactly
  // that wrong button, because a superseded row also has its flag cleared.
  if (detail.state === 'superseded') return null;

  const bar = variant === 'bar';
  const style = { height: bar ? 46 : 32, ...(bar ? { flex: 1 } : {}) };

  return (
    <div style={bar ? BAR_STYLE : HEADER_STYLE}>
      {detail.state === 'resolved' ? (
        <button type="button" className="nb-btn" style={style} onClick={() => onCompose('reopen')}>
          <RotateCcw size={14} />
          Reopen
        </button>
      ) : (
        <button
          type="button"
          className="nb-btn nb-btn-primary"
          style={style}
          onClick={() => onCompose('resolve')}
        >
          <Check size={14} />
          Mark resolved
        </button>
      )}
    </div>
  );
}
